// frontend/src/features/production/serialization/pages/SerialUnitTracePage.tsx
//
// ADR-104 Rule SN-11 — enter or scan any serial number and see its full
// traceability: genealogy tree (frozen snapshot if the WorkOrder already
// completed, otherwise the live-computed tree) plus directly linked lots.

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { GenealogyTree } from "../components/GenealogyTree";
import { useSerialUnitList, useSerialUnitTrace } from "../api/serializationApi";
import { PageBanner } from "@/shared/components/PageBanner";

export function SerialUnitTracePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("serial_number") ?? "");
  const [resolvedUnitId, setResolvedUnitId] = useState<string | undefined>(undefined);
  const [notFound, setNotFound] = useState(false);

  const { refetch: lookupBySerial } = useSerialUnitList(
    searchTerm ? { serial_number: searchTerm } : undefined
  );
  const { data: trace, isLoading } = useSerialUnitTrace(resolvedUnitId);

  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    setNotFound(false);
    setResolvedUnitId(undefined);
    const { data } = await lookupBySerial();
    const results = (data as any)?.results ?? (data as any) ?? [];
    if (results.length > 0) {
      setResolvedUnitId(results[0].id);
    } else {
      setNotFound(true);
    }
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }} dir="rtl">
      <PageBanner
        title="ردیابی شمارهٔ سریال"
        subtitle="ردیابی کامل زنجیره تولید و مشاهده قطعات تشکیل دهنده یک محصول"
        icon="🔍"
        helpLink="/help/production/serialization/serial-unit-trace"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "680px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        <BarcodeScanner
          label="شمارهٔ سریال را اسکن یا تایپ کنید"
          placeholder="SN-00001"
          onSubmit={handleSearch}
        />

        {isLoading && <p style={{ marginTop: "1rem" }}>در حال جست‌وجو…</p>}

        {notFound && (
          <p style={{ marginTop: "1rem", color: "#b91c1c", fontSize: "0.85rem" }}>
            سریالی با این شماره پیدا نشد.
          </p>
        )}

        {trace && (
          <div style={{ marginTop: "1.5rem" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2 style={{ fontSize: "0.95rem", fontWeight: 600 }}>
                زنجیرهٔ نسب
                {(trace as any).frozen ? " (فریزشده)" : " (زندهٔ فعلی — هنوز فریز نشده)"}
              </h2>
            </div>

            {(trace as any).genealogy && <GenealogyTree node={(trace as any).genealogy} />}

            {(trace as any).direct_lot_links?.length > 0 && (
              <div style={{ marginTop: "1.5rem" }}>
                <h3 style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem" }}>
                  پالت‌های مستقیم
                </h3>
                {(trace as any).direct_lot_links.map((link: any) => (
                  <div key={link.lot_id} style={{ fontSize: "0.85rem", padding: "0.35rem 0" }}>
                    <span dir="ltr">{link.lot__lot_number}</span> — مقدار دریافتی:{" "}
                    {link.lot__received_qty}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
